import {
  ReactNativeErrorBoundary,
  type FallbackComponentProps,
} from "@-/com.npmjs.react-native-error-boundary";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { captureSentryException } from "@-/dev.improve.haniverse.lib.sentry";
import type { PropsWithChildren } from "react";

/** Gorhom portals render outside page boundaries; keep their failures inside the open sheet. */
export function BottomSheetContentErrorBoundary({
  children,
  onClose,
}: PropsWithChildren<{ onClose: () => void }>) {
  return (
    <ReactNativeErrorBoundary
      FallbackComponent={({ resetError }: FallbackComponentProps) => (
        <View className="gap-3 py-4" testID="bottom-sheet-content-error">
          <Text className="font-semibold">This sheet could not open</Text>
          <Text>Try again, or close this sheet to keep using Haniverse.</Text>
          <View className="flex-row justify-end gap-3">
            <Pressable
              accessibilityRole="button"
              className="rounded-xs border border-foreground/20 px-4 py-3"
              onPress={onClose}
            >
              <Text>Close sheet</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              className="rounded-xs bg-foreground px-4 py-3"
              onPress={resetError}
            >
              <Text className="text-background">Try again</Text>
            </Pressable>
          </View>
        </View>
      )}
      onError={(error, componentStack) => {
        logger(import.meta.location).error("Bottom sheet content failed", error, componentStack);
        captureSentryException(error, {
          contexts: { react: { componentStack } },
          tags: { boundary: "bottom-sheet-content" },
        });
      }}
    >
      {children}
    </ReactNativeErrorBoundary>
  );
}
